import { Modal } from './Modal.js';
import { formatCost, maxLevel, productionLines, resourceLabel, upgradeRule } from './schema.js';
import { canCollect, canUpgrade, nextLevelCost, underConstruction } from './production.js';
import type { AssetLoader } from './AssetLoader.js';
import type { Economy } from './Economy.js';
import type { GameConfig, GameObject, Placement, PlacementState, Production } from '../types.js';

const STALLED = '--:--';

const TIMER_LABEL: Record<string, string> = {
    building: 'Time left to build',
    producing: 'Time to next output',
    stalled: 'Waiting for input resources',
};

interface BuildingWindowOptions {
    config: GameConfig;
    assetLoader: AssetLoader;
    economy?: Economy | null;
    onCollect?: ((placement: Placement) => void) | null;
    onUpgrade?: ((placement: Placement) => void) | null;
    onDemolish?: ((placement: Placement) => void) | null;
    onMove?: ((placement: Placement) => void) | null;
}

export class BuildingWindow {
    private readonly config: GameConfig;
    private readonly assetLoader: AssetLoader;
    private readonly economy: Economy | null;
    private readonly onCollect: ((placement: Placement) => void) | null;
    private readonly onUpgrade: ((placement: Placement) => void) | null;
    private readonly onDemolish: ((placement: Placement) => void) | null;
    private readonly onMove: ((placement: Placement) => void) | null;
    private readonly modal: Modal;

    private placement: Placement | null = null;

    private readonly art: HTMLElement;
    private readonly stars: HTMLElement;
    private readonly capacityBlock: HTMLElement;
    private readonly timerBlock: HTMLElement;
    private readonly rewardBlock: HTMLElement;
    private readonly inputsBlock: HTMLElement;
    private readonly demolishButton: HTMLButtonElement;
    private readonly moveButton: HTMLButtonElement;

    private starNodes: HTMLElement[] = [];
    private upgradeButton: HTMLButtonElement | null = null;
    private capacityMeters: { value: HTMLElement; fill: HTMLElement }[] = [];
    private timerValue: HTMLElement | null = null;
    private collectButton: HTMLButtonElement | null = null;

    constructor({
        config,
        assetLoader,
        economy = null,
        onCollect = null,
        onUpgrade = null,
        onDemolish = null,
        onMove = null,
    }: BuildingWindowOptions) {
        this.config = config;
        this.assetLoader = assetLoader;
        this.economy = economy;
        this.onCollect = onCollect;
        this.onUpgrade = onUpgrade;
        this.onDemolish = onDemolish;
        this.onMove = onMove;

        this.modal = new Modal({ title: '', variant: 'building' });

        const layout = document.createElement('div');
        layout.className = 'building-layout';

        const left = document.createElement('div');
        left.className = 'building-left';
        this.art = document.createElement('div');
        this.art.className = 'building-art';
        left.appendChild(this.art);
        this.stars = document.createElement('div');
        this.stars.className = 'building-stars';
        left.appendChild(this.stars);

        const right = document.createElement('div');
        right.className = 'building-right';
        this.capacityBlock = document.createElement('div');
        this.capacityBlock.className = 'building-capacity';
        right.appendChild(this.capacityBlock);
        this.timerBlock = document.createElement('div');
        this.timerBlock.className = 'building-timer';
        right.appendChild(this.timerBlock);
        this.rewardBlock = document.createElement('div');
        this.rewardBlock.className = 'building-reward';
        right.appendChild(this.rewardBlock);
        this.inputsBlock = document.createElement('div');
        this.inputsBlock.className = 'building-inputs';
        right.appendChild(this.inputsBlock);

        this.moveButton = document.createElement('button');
        this.moveButton.type = 'button';
        this.moveButton.className = 'building-move';
        this.moveButton.setAttribute('aria-label', 'Move');
        this.moveButton.textContent = '✥';
        this.moveButton.hidden = !this.onMove;
        this.moveButton.addEventListener('click', () => {
            if (!this.placement) return;
            this.onMove?.(this.placement);
        });
        right.appendChild(this.moveButton);

        this.demolishButton = document.createElement('button');
        this.demolishButton.type = 'button';
        this.demolishButton.className = 'building-demolish';
        this.demolishButton.setAttribute('aria-label', 'Demolish');
        this.demolishButton.textContent = '♻';
        this.demolishButton.hidden = !this.onDemolish;
        this.demolishButton.addEventListener('click', () => {
            if (!this.placement) return;
            this.onDemolish?.(this.placement);
        });
        right.appendChild(this.demolishButton);

        layout.appendChild(left);
        layout.appendChild(right);
        this.modal.body.appendChild(layout);
    }

    open(placement: Placement): void {
        this.placement = placement;
        this.renderStructure();
        this.renderValues();
        this.modal.open();
    }

    refresh(): void {
        if (!this.modal.isOpen || !this.placement) return;
        this.renderValues();
    }

    close(): void {
        this.modal.close();
    }

    private renderStructure(): void {
        if (!this.placement) return;
        const { object, state } = this.placement;
        const lines = productionLines(object, state.level);

        this.modal.setTitle(object.label || object.id);
        this.renderArt(object);
        this.renderStars(object, state);
        this.renderCapacity(state);
        this.renderTimer(lines);
        this.renderOutput(lines);
        this.renderInputs(lines);
    }

    private renderArt(object: GameObject): void {
        this.art.innerHTML = '';
        if (this.assetLoader.getImage(object.sprite)) {
            const img = document.createElement('img');
            img.src = this.assetLoader.resolve(object.sprite!);
            img.alt = '';
            this.art.appendChild(img);
        } else {
            const swatch = document.createElement('div');
            swatch.className = 'building-art__swatch';
            swatch.style.background = object.color ?? '#c97f00';
            this.art.appendChild(swatch);
        }
    }

    private renderStars(object: GameObject, state: PlacementState): void {
        this.stars.innerHTML = '';
        this.starNodes = [];
        this.upgradeButton = null;

        const max = maxLevel(object);
        if (max <= 1 && !upgradeRule(object, state.level)) return;

        for (let i = 0; i < max; i++) {
            const star = document.createElement('span');
            star.className = 'building-star';
            star.textContent = '★';
            this.stars.appendChild(star);
            this.starNodes.push(star);
        }

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'building-upgrade';
        button.setAttribute('aria-label', 'Upgrade');
        button.textContent = '⬆';
        button.addEventListener('click', () => {
            if (!this.placement) return;
            this.onUpgrade?.(this.placement);
            this.renderStructure();
            this.renderValues();
        });
        this.stars.appendChild(button);
        this.upgradeButton = button;
    }

    private renderCapacity(state: PlacementState): void {
        this.capacityBlock.innerHTML = '';
        this.capacityMeters = [];

        const metered = state.outputs.filter((output) => output.capacity != null);
        if (!metered.length) return;

        const heading = document.createElement('span');
        heading.className = 'building-capacity__heading';
        heading.textContent = 'CAPACITY';
        this.capacityBlock.appendChild(heading);

        state.outputs.forEach((output) => {
            if (output.capacity == null) return;

            const row = document.createElement('div');
            row.className = 'building-capacity__row';

            if (metered.length > 1) {
                const label = document.createElement('span');
                label.className = 'building-capacity__label';
                label.textContent = resourceLabel(this.config, output.resource);
                row.appendChild(label);
            }

            const value = document.createElement('span');
            value.className = 'building-capacity__value';
            row.appendChild(value);

            const track = document.createElement('div');
            track.className = 'building-bar';
            const fill = document.createElement('div');
            fill.className = 'building-bar__fill';
            track.appendChild(fill);
            row.appendChild(track);

            this.capacityBlock.appendChild(row);
            this.capacityMeters.push({ value, fill });
        });
    }

    private renderTimer(lines: Production[]): void {
        this.timerBlock.innerHTML = '';
        this.timerValue = null;
        this.collectButton = null;
        if (!lines.length) return;

        this.timerValue = document.createElement('span');
        this.timerValue.className = 'building-timer__value';
        this.timerBlock.appendChild(this.timerValue);

        const collect = document.createElement('button');
        collect.type = 'button';
        collect.className = 'building-collect';
        collect.setAttribute('aria-label', 'Collect');
        collect.textContent = '▶';
        collect.addEventListener('click', () => {
            if (!this.placement) return;
            this.onCollect?.(this.placement);
            this.renderValues();
        });
        this.timerBlock.appendChild(collect);
        this.collectButton = collect;
    }

    private renderOutput(lines: Production[]): void {
        this.rewardBlock.innerHTML = '';

        lines.forEach((line) => {
            if (!line.resource) return;
            const chip = document.createElement('span');
            chip.className = 'building-reward__chip';
            chip.textContent =
                `${line.quantity ?? 0} ${resourceLabel(this.config, line.resource)}`;
            this.rewardBlock.appendChild(chip);
        });
    }

    private renderInputs(lines: Production[]): void {
        this.inputsBlock.innerHTML = '';

        const totals = new Map<string, number>();
        lines.forEach((line) => {
            (line.inputs ?? []).forEach(({ resource, quantity }) => {
                totals.set(resource, (totals.get(resource) ?? 0) + quantity);
            });
        });

        const inputs = [...totals].map(([resource, quantity]) => ({ resource, quantity }));
        if (!inputs.length) return;

        const heading = document.createElement('span');
        heading.className = 'building-inputs__heading';
        heading.textContent = 'NEEDS';
        this.inputsBlock.appendChild(heading);

        inputs.forEach(({ resource, quantity }) => {
            const chip = document.createElement('span');
            chip.className = 'building-inputs__chip';
            chip.textContent = `${quantity} ${resourceLabel(this.config, resource)}`;
            if (this.economy && this.economy.get(resource) < quantity) {
                chip.classList.add('is-short');
            }
            this.inputsBlock.appendChild(chip);
        });
    }

    private renderValues(): void {
        if (!this.placement) return;
        const { object, state } = this.placement;

        const metered = state.outputs.filter((output) => output.capacity != null);
        this.capacityMeters.forEach((meter, index) => {
            const output = metered[index];
            if (!output) return;
            meter.value.textContent = `${output.stored}/${output.capacity}`;
            const ratio = output.capacity ? output.stored / output.capacity : 0;
            meter.fill.style.width = `${Math.min(Math.max(ratio, 0), 1) * 100}%`;
        });

        if (this.timerValue) {
            if (underConstruction(state)) {
                this.timerValue.textContent = formatDuration(state.constructionMs ?? 0);
            } else {
                const running = state.outputs
                    .map((output) => output.remainingMs)
                    .filter((ms): ms is number => ms != null && ms > 0);

                this.timerValue.textContent = running.length
                    ? formatDuration(Math.min(...running))
                    : STALLED;
            }

            const phase = underConstruction(state)
                ? 'building'
                : (this.timerValue.textContent === STALLED ? 'stalled' : 'producing');
            this.timerValue.dataset.phase = phase;
            this.timerValue.setAttribute('aria-label', TIMER_LABEL[phase]);
        }

        if (this.collectButton) {
            this.collectButton.disabled = !canCollect(this.placement);
        }

        this.starNodes.forEach((star, index) => {
            star.classList.toggle('is-filled', index < state.level);
        });

        if (this.upgradeButton) {
            const cost = nextLevelCost(object, state);
            const affordable = this.economy
                ? canUpgrade(this.placement, this.economy)
                : cost !== null;
            this.upgradeButton.disabled = !affordable;
            this.upgradeButton.title = cost
                ? `Upgrade: ${formatCost(this.config, cost)}`
                : 'Fully upgraded';
        }
    }
}

function formatDuration(ms: number): string {
    const total = Math.max(0, Math.round(ms / 1000));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
